/**
 * Friendly, human-readable shift naming for the Analytics shift breakdown.
 * Ported from `feat/alerts-list-querying`.
 *
 * The backend already labels shifts in the LOCATION-LOCAL time (e.g.
 * `shift_key: "06_12"`, `label: "06:00–12:00"` — the mock's `00_06` starts at
 * `22:00Z`, i.e. local +2). So we can classify the daypart from the local start
 * hour parsed straight off `shift_key`/`label` — no timezone plumbing, and it
 * stays correct for every location regardless of its configured tz.
 *
 * We keep the exact time range as a subtitle so no information is lost — the
 * daypart name (Night / Morning / Day / Evening) just makes the shift readable
 * at a glance for operators.
 */

import i18n from "@/shared/lib/i18n/index.ts";

export type ShiftDaypart = "night" | "morning" | "day" | "evening";

export interface ShiftDescriptor {
  daypart: ShiftDaypart;
  /** Title-case daypart name, e.g. "Night". */
  name: string;
  /** The backend's exact local window, e.g. "06:00–12:00". */
  range: string;
}

const DAYPART_KEY: Record<ShiftDaypart, "shifts.dayparts.night" | "shifts.dayparts.morning" | "shifts.dayparts.day" | "shifts.dayparts.evening"> = {
  night: "shifts.dayparts.night",
  morning: "shifts.dayparts.morning",
  day: "shifts.dayparts.day",
  evening: "shifts.dayparts.evening",
};

/**
 * Local start hour → daypart. Boundaries chosen so typical 6–8h mining shifts
 * land on the intuitive name:
 *   22:00–04:59 → Night     · 05:00–10:59 → Morning ·
 *   11:00–16:59 → Afternoon · 17:00–21:59 → Evening
 */
export function daypartForHour(hour: number): ShiftDaypart {
  const h = ((Math.trunc(hour) % 24) + 24) % 24;
  if (h >= 5 && h < 11) return "morning";
  if (h >= 11 && h < 17) return "day";
  if (h >= 17 && h < 22) return "evening";
  return "night";
}

/**
 * Parse the local start hour from `shift_key` ("06_12" → 6) or, failing that,
 * from the leading "HH:" of the label ("06:00–12:00" → 6). Returns null when
 * neither is parseable so the caller can fall back gracefully.
 */
export function shiftStartHour(shiftKey: string, label?: string): number | null {
  const fromKey = /^(\d{1,2})/.exec(shiftKey);
  if (fromKey) {
    const n = Number(fromKey[1]);
    if (Number.isFinite(n) && n >= 0 && n <= 24) return n % 24;
  }
  const fromLabel = label ? /(\d{1,2}):/.exec(label) : null;
  if (fromLabel) {
    const n = Number(fromLabel[1]);
    if (Number.isFinite(n) && n >= 0 && n <= 24) return n % 24;
  }
  return null;
}

export function describeShift(shiftKey: string, label: string): ShiftDescriptor {
  const hour = shiftStartHour(shiftKey, label);
  // Unknown start hour → don't guess a daypart; show a neutral "Shift" with the
  // raw range so nothing is mislabeled.
  if (hour == null) {
    return { daypart: "day", name: i18n.t("shifts.unknown", { ns: "alerts" }), range: label };
  }
  const daypart = daypartForHour(hour);
  return { daypart, name: i18n.t(DAYPART_KEY[daypart], { ns: "alerts" }), range: label };
}
